import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Home, Building, Calendar } from 'lucide-react';

interface NotFoundPageProps {
  type?: 'general' | 'admin' | 'interview';
  companyId?: string;
}

const NotFoundPage: React.FC<NotFoundPageProps> = ({ type = 'general', companyId }) => {
  const isAdminError = type === 'admin';
  const isInterviewError = type === 'interview';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="w-12 h-12 text-red-600" />
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>

          {isAdminError ? (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">企業IDが必要です</h2>
              <p className="text-gray-600 mb-6">
                管理画面にアクセスするには、URLに企業IDを指定してください。
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-blue-800 mb-2">
                  <Building className="w-4 h-4" />
                  <span className="font-medium">正しいURL形式</span>
                </div>
                <code className="text-sm text-blue-700 bg-blue-100 px-2 py-1 rounded">
                  /admin/[企業ID]
                </code>
              </div>
              <div className="text-sm text-gray-500 mb-6">
                例: <code className="bg-gray-100 px-2 py-1 rounded">/admin/company-001</code>
              </div>
            </div>
          ) : isInterviewError ? (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">面接URLが無効です</h2>
              <p className="text-gray-600 mb-6">
                面接にアクセスするには、正しい企業IDと面接IDを指定してください。
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-green-800 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">正しいURL形式</span>
                </div>
                <code className="text-sm text-green-700 bg-green-100 px-2 py-1 rounded">
                  /interview/[企業ID]/[面接ID]
                </code>
              </div>
              <div className="text-sm text-gray-500 mb-6">
                例:{' '}
                <code className="bg-gray-100 px-2 py-1 rounded">
                  /interview/company-001/interview-123
                </code>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  <strong>面接URL</strong>は面接担当者から提供されます。
                  <br />
                  正しいURLをご確認いただくか、面接担当者にお問い合わせください。
                </p>
              </div>
            </div>
          ) : (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">ページが見つかりません</h2>
              <p className="text-gray-600 mb-6">
                お探しのページは存在しないか、移動している可能性があります。
              </p>
            </div>
          )}

          <div className="space-y-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Home className="w-4 h-4" />
              ホームページに戻る
            </Link>

            {isAdminError && (
              <div className="text-sm text-gray-500">
                企業IDをお持ちでない場合は、管理者にお問い合わせください。
              </div>
            )}

            {isInterviewError && (
              <div className="text-sm text-gray-500">
                面接URLが不明な場合は、面接担当者にお問い合わせください。
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <p className="text-sm text-gray-500">
            何か問題がございましたら、
            <a href="mailto:support@talent-hub.com" className="text-blue-600 hover:text-blue-800">
              サポートチーム
            </a>
            までお問い合わせください。
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
